import AppResourceCollection from './AppResourceCollection'
import cloneDeep from 'lodash/cloneDeep'
import Engine from '../Engine'
import filter from 'lodash/filter'
import forEach from 'lodash/forEach'
import isEqual from 'lodash/isEqual'
import isEmpty from 'lodash/isEmpty'
import moment from 'moment'
import validate from '../Plugins/validateJs'
import EventEmitter from 'event-emitter-es6'
import {flattenKeys, hasNativeFile, parseRequestData} from './../Helpers/Misc/objectHelpers';

class AppResource {

    constructor (data, fetched = false) {
        if (typeof fetched !== 'boolean') {
            fetched = true
        }
        const { uuid, resourceType, attributes, relations } = {
            uuid: null,
            resourceType: null,
            attributes: {},
            relations: {},
            ...data,
        }
        this._emitter = new EventEmitter()
        this._dirty = {}
        this._relations = relations
        this._errors = null
        this._original = null
        this._fetched = fetched
        this._uuid = uuid
        this._resourceType = resourceType !== null
            ? resourceType
            : this.appResourceType()

        this.attributes = {}
        this.setAttributes(attributes)

        if (fetched) {
            this._original = cloneDeep(this.attributes)
            forEach(this.relations, (relation, relationName) => {
                if (this._original === null) {
                    this._original = {}
                }
                this._original[relationName] = cloneDeep(
                    this.get(relationName))
            })
        }

        this.initializeProps()
    }

    setAttributes(attributes){
      const relationMap = this.relationMap()
      const simpleAttributes = {}

      forEach(attributes, (attribute,attributeName) => {
        if(relationMap.hasOwnProperty(attributeName)){
          const relation = new (relationMap[attributeName])({})
          this.setRelation(attributeName,attribute, relation.appResourceType())
          return
        }

        simpleAttributes[attributeName] = attribute
      })

      this.attributes = simpleAttributes;
    }

    /**
     * Constants
     */

    /**
     * @returns {Array}
     */
    fillable () {
        return []
    }

    /**
     * @returns {{}}
     */
    relationMap () {
        return {}
    }

    /**
     * @returns {{}}
     */
    relationForeignKeys () {
        return {}
    }

    /**
     *
     * @returns {Array}
     */
    hidden () {
        return [
            'createdAt',
            'updatedAt',
            'deletedAt',
        ]
    }

    /**
     * @returns {string}
     */
    appResourceType () {
        return 'WildAppResource'
    }

    /**
     * Data
     */

    /**
     * @param resourceData
     * @param fetched
     * @returns {null|AppResource}
     */
    static buildFromData (resourceData, fetched = false) {
        if (!AppResource.hasAppResourceStructure(resourceData)) {
            return null
        }
        if (typeof fetched !== 'boolean') {
            fetched = true
        }
        const resourceClass = Engine.appResourceClass(resourceData.type)
        return new resourceClass(resourceData, fetched)
    }

    /**
     * @param data
     * @returns {boolean}
     */
    static hasAppResourceStructure (data) {
        return !(
            data === null ||
            typeof data !== 'object' ||
            !data.hasOwnProperty('type') ||
            typeof data.type !== 'string' ||
            !data.hasOwnProperty('attributes') ||
            typeof data.attributes !== 'object' ||
            !data.hasOwnProperty('uuid') ||
            typeof data.uuid !== 'string'
        )
    }

    /**
     * @param attName
     * @returns {boolean}
     */
    isAttribute (attName) {
        return !this.relationMap().hasOwnProperty(attName) &&
            !this.relationForeignKeys().hasOwnProperty(attName) &&
            this.validationRules().hasOwnProperty(attName)
    }

    /**
     * @param attName
     * @returns {boolean}
     */
    isRelation (attName) {
        return this.relationMap().hasOwnProperty(attName) ||
            this.relationForeignKeys().hasOwnProperty(attName)
    }

    /**
     *
     */
    initializeProps () {
        forEach(this.relationMap(), (appResourceClass, relationName) => {
            this.registerRelationProp(relationName)
        })

        forEach(this.relationForeignKeys(), (foreignKey, relationName) => {
            this.registerRelationProp(relationName)
        })

        forEach(this.validationRules(), (rules, attName) => {
            this.registerAttributeProp(attName)
        })

        forEach(this.attributes, (attribute,attName) => {
          this.registerAttributeProp(attName)
        })

    }

    /**
     *
     * @param attName
     * @returns {undefined|AppResource|AppResourceCollection|*}
     */
    get (attName) {
        if (this.attributes.hasOwnProperty(attName)) {
            return this.attributes[attName]
        }
        if (this.relations.hasOwnProperty(attName)) {
            return this.relations[attName]
        }
        return undefined
    }

    /**
     * @param remoteAppResource
     * @returns {AppResource}
     */
    syncFromRemoteAppResource (remoteAppResource) {
        if (!(remoteAppResource instanceof AppResource)) {
            return this
        }
        this.uuid = remoteAppResource.uuid
        this._dirty = {}
        this._errors = null
        this._fetched = true
        this.attributes = remoteAppResource.attributes
        this._original = cloneDeep(this.attributes)
        forEach(remoteAppResource.relations, (relation, relationName) => {
            if (this._original === null) {
                this._original = {}
            }
            this.setRelation(relationName, relation)
            this._original[relationName] = cloneDeep(this.get(relationName))
        })
        return this
    }

    /**
     * @param includingRelations
     */
    refresh (includingRelations = true) {
        forEach(this.attributes, (attValue, attName) => {
            if (!this.original.hasOwnProperty(attName)) {
                return true
            }
            this._attributes[attName] = this.original[attName]
        })

        this._dirty = {}
        if (!includingRelations) {
            return
        }
        forEach(this.relations, (relation, relationName) => {
            if (!this.original.hasOwnProperty(relationName)) {
                return true
            }
            this.setRelation(relationName, this.original[relationName])
        })
    }

    /**
     * Dirty
     */

    /**
     * @param relationName
     * @returns {boolean}
     */
    isDirtyRelation (relationName = null) {
        let relation
        for (let rel in this._relations) {
            if (
                !this._relations.hasOwnProperty(rel) ||
                this._relations[rel] === null ||
                (relationName !== null && rel !== relationName)
            ) {
                continue
            }
            relation = this._relations[rel]
            if (
                relation instanceof AppResource &&
                relation.isDirty()
            ) {
                return true
            } else if (relation instanceof AppResourceCollection) {
                let isRelationDirty = false
                forEach(relation.appResources, (resource, resourceUuid) => {
                    if (resource.isDirty()) {
                        isRelationDirty = true
                        return false
                    }
                })
                if (isRelationDirty) {
                    return true
                }
            }
        }
        return false
    }

    /**
     * @param attName
     * @returns {boolean}
     */
    isDirty (attName = null) {
        if (typeof attName === 'string') {
            return this._dirty.hasOwnProperty(attName)
        }
        if (attName === null) {
            //Check Relations
            if (this.isDirtyRelation()) {
                return true
            }
            //Check self dirty attributes
            return !isEmpty(this._dirty)
        }
        return false
    }

    /**
     *
     */
    get dirtyAttributes () {
        const keys = Object.keys(this._dirty)
        const attributes = {}
        forEach(this.attributes, (attValue, attName) => {
            if (keys.indexOf(attName) < 0) {
                return true
            }
            attributes[attName] = attValue
        })
        return attributes
    }

    /**
     * Validation
     */

    /**
     *
     * @returns {{}}
     */
    validationRules () {
        return {}
    }

    /**
     * @param relationName
     * @returns {{}|any}
     */
    validateRelation (relationName) {
        if (
            !this.relations.hasOwnProperty(relationName) ||
            isEmpty(this.relations[relationName])
        ) {
            return {}
        }

        const relation = this.relations[relationName]
        const errors = {}

        if (relation instanceof AppResource) {
            forEach(
                relation.validate(),
                (attErrors, att) => {
                    errors[relationName + '.' + att] = attErrors
                },
            )
            return errors
        }

        if (relation instanceof AppResourceCollection) {
            forEach(relation.appResources, (collectionResource, uuid) => {
                forEach(
                    collectionResource.validate(),
                    (attErrors, att) => {
                        errors[relationName + '.' + uuid + '.' +
                        att] = attErrors
                    },
                )
            })
            return errors
        }

        return this.flatErrors(errors)
    }

    /**
     * @param attributes
     * @param attRules
     * @returns {{}}
     */
    validateAttributes (attributes, attRules = null) {
        if (attRules === null || typeof attRules !== 'object') {
            attRules = this.validationRules()
            forEach(attRules, (rules, attName) => {
                if (!attributes.hasOwnProperty(attName)) {
                    delete attRules[attName]
                }
            })
        }
        let errors = validate(this.attributes, attRules)
        return this.flatErrors(errors)
    }

    /**
     * @param validateRelations
     * @returns {{any}}
     */
    validateExisting (validateRelations = true) {
        let errors = this.validateAttributes(this.attributes)
        if (validateRelations && !isEmpty(this.relations)) {
            const fillableRelations = this.fillableRelations
            forEach(this.relations, (relation, relationName) => {
                forEach(
                    this.validateRelation(relationName),
                    (attErrors, att) => {
                        errors[att] = attErrors
                    },
                )
            })
        }
        return this.flatErrors(errors)
    }

    /**
     * @param validateRelations
     * @returns {any}
     */
    validate (validateRelations = false) {
        if (this.exists && isEmpty(this._dirty) && !validateRelations) {
            return {}
        }

        if (this.exists && isEmpty(this._dirty) && validateRelations && !this.isDirtyRelation()) {
            return {}
        }

        let errors = this.validateAttributes(this.attributes,
            this.validationRules())
        if (validateRelations && !isEmpty(this.relations)) {
            const fillableRelations = this.fillableRelations
            forEach(this.relations, (relation, relationName) => {
                forEach(
                    this.validateRelation(relationName),
                    (attErrors, att) => {
                        errors[att] = attErrors
                    },
                )
            })
        }

        return this.flatErrors(errors)
    }

    /**
     * @param errors
     * @returns {{}|*}
     */
    flatErrors (errors) {
        if (typeof errors !== 'object' || isEmpty(errors)) {
            return {}
        }
        forEach(errors, (attErrors, attName) => {
            if (attErrors.length === 1) {
                errors[attName] = attErrors[0]
                return true
            }
            errors[attName] = attErrors
        })
        return errors
    }

    /**
     *
     * @returns {boolean}
     */
    get isValid () {
        const errors = this.validate()
        if (!isEmpty(errors)) {
            this._errors = errors
            return false
        }
        return true
    }

    /**
     *
     * @returns {null}
     */
    get errors () {
        return this._errors === null ?
            this.validate() :
            this._errors
    }

    /**
     *
     * @returns {[]|Array}
     */
    attributeErrors (attName) {
        if (
            isEmpty(this.errors) ||
            !this.errors.hasOwnProperty(attName)
        ) {
            return []
        }
        return this.errors[attName]
    }

    /**
     *  Relations
     */

    /**
     *
     * @param relationName
     * @param relationData
     */
    setRelation (relationName, relationData, relationType) {
        let singleResource

        this.registerRelationProp(relationName)

        const isRawCollection = relationData &&
          !Array.isArray(relationData) &&
          !(relationData instanceof AppResource) &&
          !(relationData instanceof AppResourceCollection) &&
          !relationData.hasOwnProperty('attributes');

        if(isRawCollection) {
          relationData = Object.values(relationData)
        }

        if (Array.isArray(relationData)) {
            if (!relationData.length) {
                this._relations[relationName] = new AppResourceCollection(relationType)
                return
            }
            const relationResources = new AppResourceCollection(
                relationData[0].type)
            for (let i = 0; i < relationData.length; i++) {
                singleResource = AppResource.buildFromData(relationData[i],
                    true)
                if (singleResource === null) {
                    continue
                }
                relationResources.addResource(singleResource)
            }
            this._relations[relationName] = relationResources
            return
        }


        if (typeof relationData !== 'object') {
            this._relations[relationData] = null
            return
        }

        if (relationData instanceof AppResource) {
            this._relations[relationName] = relationData
            return
        }

        singleResource = AppResource.buildFromData(relationData, true)
        if (singleResource !== null) {
            this._relations[relationName] = singleResource
            return
        }

        if (relationData instanceof AppResourceCollection) {
            this._relations[relationName] = relationData
        }
    }

    /**
     * @returns {{}|*}
     */
    get relations () {
        return this._relations
    }

    /**
     * @returns {string[]}
     */
    get fillableRelations () {
        return Object.keys(this.relationForeignKeys()).
            concat(Object.keys(this.relationMap())).
            concat(Object.keys(this.relations)).
            filter(item => {
                return this.fillable().indexOf(item) > -1
            })
    }

    /**
     * @param relationName
     */
    registerRelationProp (relationName) {
        if (!this.hasOwnProperty(relationName)) {
            this[relationName] = null
            Object.defineProperty(this, relationName, {
                get: () => {
                    return this.get(relationName)
                },
                set: (value) => {
                    this.setRelation(relationName, value)
                },
            })
        }
    }

    /**
     *  Attributes
     */

    /**
     * @returns {{}|*}
     */
    get attributes () {
        return this._attributes
    }

    /**
     * @param attributeName
     * @param attributeValue
     * @returns {moment.Moment|number|*}
     */
    castAttribute (attributeName, attributeValue) {
        if (isEmpty(attributeValue)) {
            return attributeValue
        }
        const rules = this.validationRules().hasOwnProperty(attributeName) ?
            this.validationRules()[attributeName] :
            {}

        if (isEmpty(rules)) {
            return attributeValue
        }

        if (rules.hasOwnProperty('type')) {
            switch (rules.type) {
                case 'integer':
                    return parseInt(attributeValue)
                case 'number':
                    return parseFloat(attributeValue)
                case 'date':
                    return moment(attributeValue)
                case 'boolean':
                    if (typeof attributeValue === 'boolean') {
                        return attributeValue
                    }
                    return attributeValue !== 'false'
            }
        }

        if (rules.hasOwnProperty('numericality')) {
            const numericality = rules.numericality
            if (typeof numericality === 'boolean') {
                return parseFloat(attributeValue)
            }
            if (typeof numericality === 'object') {
                if (numericality.hasOwnProperty('onlyInteger')) {
                    return parseInt(attributeValue)
                }
            }
            return parseFloat(attributeValue)
        }

        if (
            rules.hasOwnProperty('datetime') ||
            rules.hasOwnProperty('date')
        ) {
            return moment(attributeValue)
        }

        return attributeValue
    }

    /**
     * @param attributes
     */
    set attributes (attributes) {

        this._attributes = {}

        if (typeof attributes !== 'object') {
            return
        }

        for (let attName in attributes) {
            if (!attributes.hasOwnProperty(attName)) {
                continue
            }
            this.setAttribute(attName, attributes[attName])
        }
    }

    /**
     * @param attributes
     */
    mergeAttributes (attributes) {
        if (typeof attributes !== 'object') {
            return
        }
        for (let attName in attributes) {
            if (!attributes.hasOwnProperty(attName)) {
                continue
            }
            this.setAttribute(attName, attributes[attName])
        }
    }

    /**
     * @param attName
     * @param attValue
     * @returns {AppResource}
     */
    setAttribute (attName, attValue) {

        if (typeof attValue === 'object') {
            if (attValue === null &&
                this.relationMap().hasOwnProperty(attName)) {
                this.setRelation(attName, attValue)
                return this
            }

            if (attValue instanceof AppResource) {
                this.setRelation(attName, attValue)
                return this
            }

            if (AppResource.hasAppResourceStructure(attValue)) {
                this.setRelation(attName, attValue)
                return this
            }

            const extractedResourceData = filter(attValue, (object, key) => {
                return AppResource.hasAppResourceStructure(object)
            })
            if (!isEmpty(extractedResourceData)) {
                this.setRelation(attName, extractedResourceData)
                return this
            }
        }

        if (
            Array.isArray(attValue) &&
            attValue.length &&
            AppResource.hasAppResourceStructure(attValue[0])
        ) {
            this.setRelation(attName, attValue)
            return this
        }

        attValue = this.castAttribute(attName, attValue)
        if (
            this._original !== null  &&
            attName.includes('.') &&
            !isEqual(attValue, _.get( this._original, attName))
        ) {
            let databaseFieldName = attName.substr(0, attName.indexOf('.'));
            if(!this._dirty.hasOwnProperty(databaseFieldName)){
                this._dirty[databaseFieldName] = {};
            }
            this._dirty[databaseFieldName]= _.get( this._original, attName)
        }
        if (
            this._original !== null &&
            this._original.hasOwnProperty(attName) &&
            !isEqual(attValue, this._original[attName])
        ) {
            this._dirty[attName] = this._original[attName];
        } else if (
            this.fillable().includes(attName) &&
            this._attributes[attName]
        ) {
            this._dirty[attName] = true;
        }
        this._attributes[attName] = attValue

        this.registerAttributeProp(attName)

        const validationErrors = this.validateAttributes(this.attributes)
        if (!isEmpty(validationErrors)) {
            this._errors = validationErrors
        } else if (
            this._errors !== null &&
            this._errors.hasOwnProperty(attName)
        ) {
            delete this._errors[attName]
        }

        return this
    }

    /**
     * @param attName
     */
    registerAttributeProp (attName) {
        if (!this.hasOwnProperty(attName)) {
            this[attName] = null
            Object.defineProperty(this, attName, {
                get: () => {
                    return this.get(attName)
                },
                set: (value) => {
                    this.setAttribute(attName, value)
                },
            })
        }
    }

    /**
     * API
     */

    /**
     * @returns {string}
     */
    appResourceApiPrefix () {
        return ''
    }

    /**
     * @returns {{search: *, restore: *, fetch: *, create: *, update: *, delete: *}}
     */
    apiPaths (environment = null) {
        return {
            'search': Engine.apiUrl(this.appResourceApiPrefix() + '/search', environment),
            'create': Engine.apiUrl(this.appResourceApiPrefix(), environment),
            'update': Engine.apiUrl(this.appResourceApiPrefix() + '/{uuid}', environment),
            'delete': Engine.apiUrl(this.appResourceApiPrefix() + '/{uuid}', environment),
            'restore': Engine.apiUrl(this.appResourceApiPrefix() + '/{uuid}/restore', environment),
            'fetch': Engine.apiUrl(this.appResourceApiPrefix() + '/{uuid}', environment),
            'copy': Engine.apiUrl(this.appResourceApiPrefix() + '/{uuid}/copy', environment),
            'batchCreate': Engine.apiUrl(this.appResourceApiPrefix() + '/batch-create', environment),
            'batchUpdate': Engine.apiUrl(this.appResourceApiPrefix() + '/batch-update', environment),
            'batchDelete': Engine.apiUrl(this.appResourceApiPrefix() + '/batch-delete', environment),
            'batchRestore': Engine.apiUrl(this.appResourceApiPrefix() + '/batch-restore', environment),
        }
    }

    /**
     *
     * @param relations
     * @returns {Promise<ApiResponse|null>}
     */
    async load (relations, environment = null) {
        if (typeof relations === 'string') {
            relations = [relations]
        } else if (!Array.isArray(relations)) {
            relations = []
        }
        if (isEmpty(relations)) {
            return Promise.resolve(this)
        }
        return this.fetch(relations, environment)
    }

    /**
     * @param include
     * @returns {Promise<ApiResponse|null>}
     */
    async find (include = [], environment) {
        return this.fetch(include, environment)
    }

    /**
     * @returns {Promise<ApiResponse|null>}
     */
    async fetch (include = [], environment = null) {
        if (this.uuid === null) {
            return Promise.resolve(null)
        }
        const url = this.apiPaths(environment).fetch.replace('{uuid}', this.uuid)
        const getParams = {}
        if (Array.isArray(include) && include.length) {
            getParams.include = include
        }

        this._emitter.emitSync('beforeFetch', this, getParams)

        const apiResponse = await Engine.get(url, {
            params: getParams,
        })

        if (isEmpty(apiResponse.resources) ||
            !apiResponse.resources.has(this.uuid)) {
            return Promise.resolve(null)
        }

        this.syncFromRemoteAppResource(apiResponse.resources.get(this.uuid))

        this._emitter.emit('fetched', this, apiResponse)

        return apiResponse
    }

    /**
     * @returns {Promise<ApiResponse>}
     */
    async create (includingRelations = true, validateRelations = true, environment = null) {
        if (this.exists) {
            return Promise.resolve(null)
        }

        const validationErrors = this.validate(validateRelations)
        if (!isEmpty(validationErrors)) {
            const resourceType = this.resourceType
            if (Engine.debug) {
                console.warn(
                    'Cannot create invalid ' + resourceType,
                    validationErrors,
                )
            }
            return Promise.resolve(null)
        }

        const url = this.apiPaths(environment).create

        let createData = this.buildCreateData(includingRelations)

        this._emitter.emitSync('beforeCreate', this, createData)
        this._emitter.emitSync('beforeSave', this, createData)

        createData = parseRequestData(createData);

        const apiResponse = await this.enginePost(url, createData)
        if (isEmpty(apiResponse.resources)) {
            return Promise.resolve(null)
        }
        forEach(apiResponse.resources.appResources, (appResource, uuid) => {
            this.syncFromRemoteAppResource(appResource)
        })

        this._emitter.emit('created', this, apiResponse)
        this._emitter.emit('saved', this, apiResponse)

        return apiResponse
    }

    async enginePost(url, data, config){
        return Engine.post(url, data, config);
    }

    /**
     * @returns {Promise<ApiResponse|null>}
     */
    async delete (environment = null) {
        if (this.uuid === null) {
            return Promise.resolve(null)
        }

        const url = this.apiPaths(environment).delete.replace('{uuid}', this.uuid)

        this._emitter.emitSync('beforeDelete', this)
        const apiResponse = await Engine.delete(url)
        this._emitter.emit('deleted', this)
        return apiResponse
    }

    /**
     * @returns {Promise<ApiResponse|null>}
     */
    async deleteWithParams(environment = null, params = {}) {
        if (this.uuid === null) {
            return Promise.resolve(null)
        }

        const url = this.apiPaths(environment).delete.replace('{uuid}', this.uuid)
        params['_method'] = 'DELETE';

        this._emitter.emitSync('beforeDelete', this)
        const apiResponse = await Engine.post(url, params)
        this._emitter.emit('deleted', this)
        return apiResponse
    }

    /**
     * @returns {Promise<ApiResponse|null>}
     */
    async restore (environment = null) {
        if (this.uuid === null) {
            return Promise.resolve(null)
        }
        const url = this.apiPaths(environment).restore.replace('{uuid}', this.uuid)
        this._emitter.emitSync('beforeRestore', this)
        const apiResponse = await this.enginePost(url)
        if (isEmpty(apiResponse.resources) ||
            !apiResponse.resources.has(this.uuid)) {
            this._emitter.emit('restored', this, apiResponse)
            return apiResponse
        }
        this.syncFromRemoteAppResource(apiResponse.resources.get(this.uuid))
        this._emitter.emit('restored', this, apiResponse)
        return apiResponse
    }

     relationObjectTree() {
       const relations = {}

        forEach(this.relations, (resourceRelation, relationName) => {
          if(!relations.hasOwnProperty(relationName)){
            relations[relationName] = {}
          }

          if(resourceRelation instanceof AppResource){
            const subRelations = resourceRelation.relationObjectTree()

            forEach(subRelations,(subRelation,subRelationName) => {
              relations[relationName][subRelationName] = {
                ...relations[relationName][subRelationName],
                ...subRelation
              }
            })
          } else if(resourceRelation instanceof AppResourceCollection){
            resourceRelation.forEach((subRelation) => {
              const subRelations = subRelation.relationObjectTree()
              forEach(subRelations,(subRelations,subRelationName) => {
                relations[relationName][subRelationName] = {
                  ...relations[relationName][subRelationName],
                  ...subRelations
                }
              })
            })
          }
        })

       return relations
     }
    /**
     * @param includingRelations
     * @returns {Promise<ApiResponse|null>}
     */
    async update (includingRelations = false, validateRelations = true, environment = null) {

        if (typeof includingRelations !== 'boolean') {
            includingRelations = true
        }

        if (
            !this.exists ||
            !this.isDirty()
        ) {
            return Promise.resolve(null)
        }

        const validationErrors = this.validateExisting(validateRelations)
        if (!isEmpty(validationErrors)) {
            if (Engine.debug) {
                const resourceType = this.resourceType
                console.warn(
                    'Cannot update invalid ' + resourceType,
                    validationErrors,
                )
            }
            return Promise.resolve(null)
        }

        if (includingRelations && this.isDirtyRelation()) {
            let relation
            for (let relationName in this._relations) {
                if (
                    !this._relations.hasOwnProperty(relationName) ||
                    this._relations[relationName] === null ||
                    !this.isDirtyRelation(relationName)
                ) {
                    continue
                }
                relation = this._relations[relationName]
                if (relation instanceof AppResourceCollection) {
                    let relationAppResource
                    for (let relationUuid in relation.appResources) {
                        if (!relation.appResources.hasOwnProperty(
                            relationUuid)) {
                            continue
                        }
                        relationAppResource = relation.get(relationUuid)
                        await relationAppResource.update(includingRelations, environment)
                    }
                    continue
                }
                if (!(relation instanceof AppResource)) {
                    continue
                }
                await relation.update(includingRelations, environment)
            }
        }

        const url = this.apiPaths(environment).update.replace('{uuid}', this.uuid)
        let updateData = this.buildUpdateData(includingRelations)

        if (!isEmpty(updateData) && !isEmpty(this.relations)) {
            updateData['include'] = flattenKeys(this.relationObjectTree())
        }

        if (!isEmpty(updateData)) {
            this._emitter.emitSync('beforeUpdate', this, updateData)
            this._emitter.emitSync('beforeSave', this, updateData)

            updateData = parseRequestData(updateData);

            const apiResponse = await this.enginePost(url, updateData)
            if (isEmpty(apiResponse.resources) ||
                !apiResponse.resources.has(this.uuid)) {
                this._emitter.emit('updated', this, apiResponse)
                this._emitter.emit('saved', this, apiResponse)
                return apiResponse
            }
            this.syncFromRemoteAppResource(
                apiResponse.resources.get(this.uuid))
            this._emitter.emit('updated', this, apiResponse)
            this._emitter.emit('saved', this, apiResponse)
            return apiResponse
        }

        return Promise.resolve(null)
    }

    /**
     * @returns {Promise<ApiResponse|null>}
     */
    async copy (environment = null) {
        if (this.uuid === null) {
            return Promise.resolve(null)
        }

        let updateData = {};

        if(this.isDirty){
            const validationErrors = this.validateExisting(false);
            if (!isEmpty(validationErrors)) {
                if (Engine.debug) {
                    const resourceType = this.resourceType;
                    console.warn(
                      'Cannot update invalid ' + resourceType,
                      validationErrors,
                    )
                }
                return Promise.reject('Attributes validation not passed!')
            }
            updateData = this.buildUpdateData(false)
        }

        const url = this.apiPaths(environment).copy.replace('{uuid}', this.uuid);

        this._emitter.emitSync('beforeCopy', this);
        const apiResponse = await this.enginePost(url, updateData)
        this._emitter.emit('copied', this, apiResponse);

        return apiResponse
    }
    /**
     * @param includingRelations
     * @returns {Promise<void>}
     */
    async save (includingRelations = false, validateRelations = true, environment = null) {
        if (!this.isValid) {
            return
        }
        if (this.exists) {
            await this.update(includingRelations, validateRelations, environment)
            return
        }
        await this.create(includingRelations, validateRelations, environment)
    }

    /**
     * @param includingRelations
     * @return {*}
     */
    buildCreateData (includingRelations = true) {
        const createData = {};

        for (let attName in this.attributes) {
            if (
              !this.attributes.hasOwnProperty(attName) ||
              this.fillable().indexOf(attName) < 0
            ) {
                continue
            }

            if(this.attributes[attName] instanceof moment) {
                let dateFormat = this.getAPIDatetimeFormat()[attName] ? this.getAPIDatetimeFormat()[attName] : 'YYYY-MM-DD HH:mm:ss';
                createData[attName] = this.attributes[attName].format(dateFormat);
            } else {
                createData[attName] = this.attributes[attName];
            }
        }

        if (includingRelations) {
            forEach(this.relations, (relation, relationName) => {
                if (
                  this.fillable().indexOf(relationName) < 0 ||
                  this.hidden().indexOf(relationName) > -1
                ) {
                    return true
                }

                if (relation instanceof AppResource) {
                    if (!relation.exists) {
                        createData[relationName] = relation.buildCreateData(
                          includingRelations)
                    }
                    return true
                }

                if (relation instanceof AppResourceCollection) {
                    createData[relationName] = []
                    relation.each((relationResource, uuid) => {
                        if (relationResource.exists) {
                            createData[relationName].push(
                              relationResource.uuid)
                            return true
                        }

                        createData[relationName].push(
                          relationResource.buildCreateData(
                            includingRelations))
                    })
                }

            })
        }
        
        return createData
    }

    /**
     * @param includingRelations
     * @returns {*}
     */
    buildUpdateData (includingRelations = true) {
        let updateData = this.buildCreateData(includingRelations)
        for (let attName in updateData) {
            if (
                !updateData.hasOwnProperty(attName) ||
                !this.attributes.hasOwnProperty(attName) ||
                this.isDirty(attName)
            ) {
                continue
            }
            delete updateData[attName]
        }

        return updateData
    }

    /**
     *
     * @param relationName
     * @param resources
     * @returns {Promise<void>}
     */
    async sync (relationName, resources = [], save = true, validateRelations = true, environment = null) {
        if (!(this[relationName] instanceof AppResourceCollection)) {
        //    return
        }

        if (!Array.isArray(resources)) {
            resources = [resources]
        }

        const relationResourceClass = this.relationMap()[relationName]
        const collection = new AppResourceCollection(
            (new relationResourceClass).appResourceType())

        for (const resource of resources) {
            if (typeof resource === 'string') {
                //those temp resources will be updated by request after save.
                const tmpResource = new relationResourceClass(
                    { uuid: resource },
                    true,
                )
                tmpResource._dirty[Math.random()] = true
                collection.addResource(tmpResource)
            } else if (resource instanceof relationResourceClass) {
                collection.addResource(resource)
            }
        }

        this.setRelation(relationName, collection)
        if (save) {
            return await this.save(true, validateRelations, environment)
        }
    }

    /**
     *  Events
     */

    /**
     * @param eventName
     * @param listener
     */
    on (eventName, listener) {
        this._emitter.on(eventName, listener)
    }

    /**
     * @param eventName
     * @param listener
     */
    once (eventName, listener) {
        this._emitter.once(eventName, listener)
    }

    /**
     * @param eventName
     * @param listener
     */
    off (eventName, listener) {
        this._emitter.off(eventName, listener)
    }

    /**
     * Getters & Setters
     */

    /**
     * @returns {boolean}
     */
    get exists () {
        return this._fetched && this.uuid !== null
    }

    /**
     * @returns {string|null}
     */
    get uuid () {
        return this._uuid
    }

    set uuid (uuid) {
        this._uuid = uuid
    }

    get resourceType () {
        return this._resourceType
    }

    get original () {
        return this._original === null ? {} : this._original
    }

    getAPIDatetimeFormat() {
        return {};
    }

}

export default AppResource
